import { useEffect, useRef, useState, type ReactNode } from 'react';

import { ApiError } from '../../api/client';
import type { FigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { useFigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import {
  attachCarrierDocument,
  deleteAsset,
  getCarrierDocuments,
  getCarrierProfile,
  submitCarrierVerification,
  updateCarrierVehicle,
  uploadAsset,
  type CarrierDocument,
  type CarrierProfile,
  type DocumentType,
  type FileAsset,
} from '../../features/carrierVerification/api';
import { useAuth } from '../../state/auth';
import { Button, Footer, Header, Input, WizardButton } from '../../ui';
import type { ScreenId } from '../../app/screenRoutes';
import '../RequestWizard/request-wizard.css';
import '../StandaloneAuth/standalone-auth.css';
import './carrier-verification.css';

export type CarrierVerificationScreenId = Extract<ScreenId, 'carrier-verify' | 'carrier-verify-pending'>;

const SCREEN_NODES: Record<CarrierVerificationScreenId, Partial<Record<FigmaBreakpoint, string>>> = {
  'carrier-verify': { '1920': '2787:50363', '1440': '2787:50211' },
  'carrier-verify-pending': { '1920': '2787:50460', '1440': '2787:50308' },
};

const FEATURE_OPTIONS = ['Кондиционер', 'Отдельный отсек', 'Фиксация переноски', 'Поилка в пути'];

function navigate(screen: ScreenId) {
  window.location.hash = `#${screen}`;
}

function blockNode(breakpoint: FigmaBreakpoint, desktop: string, compact: string) {
  return breakpoint === '1920' ? desktop : compact;
}

function errorMessage(error: unknown): string {
  let msg = '';
  if (error instanceof ApiError && error.payload && typeof error.payload === 'object') {
    const first = Object.values(error.payload as Record<string, unknown>)[0];
    if (Array.isArray(first) && first.length) msg = String(first[0]);
    else if (typeof first === 'string') msg = first;
  }
  if (!msg) {
    msg = error instanceof Error ? error.message : 'Не удалось выполнить действие';
  }

  if (/Ensure this value is greater than or equal to 1900/i.test(msg)) {
    return 'Укажите корректный год выпуска авто (не ранее 1900 года)';
  }
  if (/Ensure this value is less than or equal to/i.test(msg)) {
    return 'Укажите корректный год выпуска авто';
  }

  return msg.replace(/\.+$/, '');
}

function VerificationProgress() {
  return (
    <div className="request-wizard__progress carrier-verification__progress" data-figma-node="124:32118">
      <div className="request-wizard__progress-bars" data-figma-variant="Step=6">
        {Array.from({ length: 6 }, (_, index) => <i className="is-active" key={index} />)}
      </div>
      <div className="request-wizard__progress-meta carrier-verification__progress-meta">
        <Button kind="tertiary" size="medium" icon="left" onClick={() => navigate('carrier-rega-password')}>Назад</Button>
        <span className="standalone-auth__progress-label">
          <b>Регистрация перевозчика (6/6)</b><i aria-hidden="true" /><span>верификация</span>
        </span>
      </div>
    </div>
  );
}

function VerificationShell({
  breakpoint,
  children,
  screen,
}: {
  breakpoint: FigmaBreakpoint;
  children: ReactNode;
  screen: CarrierVerificationScreenId;
}) {
  const node = SCREEN_NODES[screen][breakpoint]
    ?? SCREEN_NODES[screen]['1440']
    ?? SCREEN_NODES[screen]['1920'];
  const long = screen === 'carrier-verify';

  return (
    <section
      className={`request-wizard carrier-verification carrier-verification--${screen}`}
      data-screen={screen}
      data-figma-node={node}
      data-figma-variant={breakpoint}
    >
      <div className={`request-wizard__stage carrier-verification__stage${long ? ' carrier-verification__stage--long' : ''}`}>
        <Header
          onLogoClick={() => navigate('landing')}
          onLogin={() => navigate('login')}
          onCarrierRegistration={() => navigate('carrier-rega-email')}
        />
        <div className={`request-wizard__layout carrier-verification__layout${long ? ' carrier-verification__layout--long' : ''}`}>
          <div className="request-wizard__image carrier-verification__image" role="img" aria-label="Дорога к месту назначения" />
          <main className={`request-wizard__form-shell carrier-verification__form-shell${long ? ' carrier-verification__form-shell--long' : ''}`}>
            {long && <VerificationProgress />}
            {children}
          </main>
        </div>
      </div>
      <Footer onNavigate={(action) => {
        if (action === 'logo') navigate('landing');
        if (action === 'customer-cabinet') navigate('my-requests');
        if (action === 'carrier-cabinet') navigate('carrier-dashboard');
        if (action === 'carrier-registration') navigate('carrier-rega-email');
      }} />
    </section>
  );
}

function UploadTrigger({
  accept,
  children,
  className,
  disabled,
  multiple,
  node,
  onFiles,
}: {
  accept: string;
  children: ReactNode;
  className: string;
  disabled?: boolean;
  multiple?: boolean;
  node?: string;
  onFiles: (files: File[]) => void | Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <button className={className} data-figma-node={node} disabled={disabled} onClick={() => inputRef.current?.click()} type="button">
        {children}
      </button>
      <input
        accept={accept}
        hidden
        multiple={multiple}
        ref={inputRef}
        type="file"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = '';
          if (files.length) void onFiles(files);
        }}
      />
    </>
  );
}

function DocumentUpload({
  document,
  documentType,
  disabled,
  label,
  node,
  onUpload,
  title,
}: {
  document?: CarrierDocument;
  documentType: DocumentType;
  disabled: boolean;
  label: string;
  node?: string;
  onUpload: (file: File, type: DocumentType, title?: string) => Promise<void>;
  title?: string;
}) {
  return (
    <div className="carrier-verification__document-field">
      <span>{label}</span>
      <UploadTrigger
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="carrier-verification__document-button"
        disabled={disabled}
        node={node}
        onFiles={async ([file]) => onUpload(file, documentType, title)}
      >
        {document ? 'Загружено' : 'Загрузить'}
      </UploadTrigger>
    </div>
  );
}

function VerificationForm({ breakpoint }: { breakpoint: FigmaBreakpoint }) {
  const [profile, setProfile] = useState<CarrierProfile | null>(null);
  const [documents, setDocuments] = useState<CarrierDocument[]>([]);
  const [makeModel, setMakeModel] = useState('');
  const [year, setYear] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [bodyType, setBodyType] = useState('Универсал');
  const [features, setFeatures] = useState<string[]>([]);
  const [licenseTitles, setLicenseTitles] = useState(['', '']);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const { status, user } = useAuth();

  useEffect(() => {
    if (status === 'guest') {
      navigate('login');
      return;
    }
    if (status !== 'authenticated') return;
    if (!user?.has_carrier_profile) {
      navigate('carrier-rega-email');
      return;
    }

    let active = true;
    Promise.all([getCarrierProfile(), getCarrierDocuments()])
      .then(([nextProfile, nextDocuments]) => {
        if (!active) return;
        const vehicle = nextProfile.vehicles[0];
        setProfile(nextProfile);
        setDocuments(nextDocuments);
        setMakeModel(vehicle?.make_model ?? '');
        setYear(vehicle?.year ? String(vehicle.year) : '');
        setPlateNumber(vehicle?.plate_number ?? '');
        setBodyType(vehicle?.body_type || 'Универсал');
        setFeatures(vehicle?.pet_features ? vehicle.pet_features.split(', ').filter(Boolean) : []);
        setLoaded(true);
      })
      .catch((nextError) => {
        if (!active) return;
        setError(errorMessage(nextError));
        setLoaded(true);
      });
    return () => { active = false; };
  }, [status, user]);

  if (!loaded) {
    return <div className="carrier-verification__loading" role="status">Загрузка…</div>;
  }

  const uploadPhotos = async (files: File[], purpose: 'vehicle_photo' | 'portfolio') => {
    setBusy(true);
    setError('');
    try {
      const uploaded: FileAsset[] = [];
      for (const file of files) uploaded.push(await uploadAsset(file, purpose, 'public'));
      setProfile((current) => current ? {
        ...current,
        [purpose === 'vehicle_photo' ? 'vehicle_photos' : 'portfolio_photos']: [
          ...current[purpose === 'vehicle_photo' ? 'vehicle_photos' : 'portfolio_photos'],
          ...uploaded,
        ],
      } : current);
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const uploadDocument = async (file: File, type: DocumentType, title = '') => {
    setBusy(true);
    setError('');
    try {
      const asset = await uploadAsset(file, 'document', 'private');
      const document = await attachCarrierDocument(type, asset.id, title);
      setDocuments((current) => [...current, document]);
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (asset: FileAsset) => {
    setBusy(true);
    setError('');
    try {
      await deleteAsset(asset.id);
      setProfile((current) => current ? {
        ...current,
        vehicle_photos: current.vehicle_photos.filter((item) => item.id !== asset.id),
        portfolio_photos: current.portfolio_photos.filter((item) => item.id !== asset.id),
      } : current);
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!makeModel.trim()) return setError('Укажите марку и модель транспорта.');
    if (year) {
      const numYear = Number(year);
      const currentYear = new Date().getFullYear();
      if (Number.isNaN(numYear) || numYear < 1900 || numYear > currentYear) {
        return setError(`Укажите корректный год выпуска авто (1900–${currentYear}).`);
      }
    }
    if (!documents.length) return setError('Загрузите хотя бы один документ.');
    setBusy(true);
    setError('');
    try {
      await updateCarrierVehicle({
        vehicle_body_type: bodyType,
        vehicle_make_model: makeModel.trim(),
        vehicle_pet_features: features.join(', '),
        vehicle_plate_number: plateNumber.trim(),
        vehicle_year: year ? Number(year) : null,
      });
      const verification = await submitCarrierVerification();
      navigate(verification.verification_status === 'approved' ? 'carrier-profile-internal' : 'carrier-verify-pending');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const transportNode = blockNode(breakpoint, '2787:50382', '2787:50230');
  const documentsNode = blockNode(breakpoint, '2787:50401', '2787:50249');
  const portfolioNode = blockNode(breakpoint, '2787:50429', '2787:50277');
  const identity = documents.find((item) => item.document_type === 'identity');
  const selfie = documents.find((item) => item.document_type === 'selfie');
  const driverLicense = documents.find((item) => item.document_type === 'driver_license');
  const vehicleRegistration = documents.find((item) => item.document_type === 'vehicle_registration');
  const companyDocument = documents.find((item) => item.document_type === 'company');
  const licenses = documents.filter((item) => item.document_type === 'license');

  return (
    <div className="carrier-verification__form" data-figma-node={blockNode(breakpoint, '2787:50377', '2787:50225')}>
      <div className="carrier-verification__copy">
        <h1>Последний шаг — документы</h1>
        <p>Без верификации откликаться на заявки нельзя — клиенты доверяют только проверенным. Модерация занимает до 24 часов.</p>
      </div>

      <section className="carrier-verification__card carrier-verification__transport" data-figma-node={transportNode}>
        <h2>Транспорт</h2>
        <div className="carrier-verification__field-row">
          <Input density="compact" label="Марка и модель" placeholder="Volkswagen Caddy" value={makeModel} onChange={(event) => setMakeModel(event.target.value)} />
          <Input density="compact" label="Год" inputMode="numeric" placeholder="2020" value={year} onChange={(event) => setYear(event.target.value.replace(/\D/g, '').slice(0, 4))} />
        </div>
        <div className="carrier-verification__field-row">
          <Input density="compact" label="Гос. номер" placeholder="А777ВВ77" value={plateNumber} onChange={(event) => setPlateNumber(event.target.value)} />
          <label className="carrier-verification__select">
            <span>Тип кузова</span>
            <select value={bodyType} onChange={(event) => setBodyType(event.target.value)}>
              <option>Универсал</option>
              <option>Минивэн</option>
              <option>Микроавтобус</option>
              <option>Грузовой фургон</option>
            </select>
          </label>
        </div>
        <div className="carrier-verification__features">
          <span>Особенности для животных</span>
          <div data-figma-node={blockNode(breakpoint, '2787:50392', '2787:50240')}>
            {FEATURE_OPTIONS.map((feature) => {
              const active = features.includes(feature);
              return (
                <button
                  aria-pressed={active}
                  className={active ? 'is-active' : ''}
                  key={feature}
                  onClick={() => setFeatures((current) => active ? current.filter((item) => item !== feature) : [...current, feature])}
                  type="button"
                >
                  {feature}
                </button>
              );
            })}
          </div>
        </div>
        <div className="carrier-verification__vehicle-upload">
          <span>Фото авто (внешний вид + салон)</span>
          <UploadTrigger
            accept="image/jpeg,image/png,image/webp"
            className="carrier-verification__dropzone carrier-verification__dropzone--vehicle"
            disabled={busy}
            multiple
            node={blockNode(breakpoint, '2787:50399', '2787:50247')}
            onFiles={(files) => uploadPhotos(files.slice(0, Math.max(0, 6 - (profile?.vehicle_photos.length ?? 0))), 'vehicle_photo')}
          >
            Загрузите 3–6 фото
          </UploadTrigger>
          {(profile?.vehicle_photos ?? []).length > 0 && (
            <div className="carrier-verification__photo-grid carrier-verification__photo-grid--vehicle">
              {(profile?.vehicle_photos ?? []).slice(0, 6).map((asset, index) => (
                <figure key={asset.id}>
                  <img src={asset.url?.startsWith('http') || asset.url?.startsWith('/') ? asset.url : `/${asset.url}`} alt={`Фото авто ${index + 1}`} />
                  <button aria-label={`Удалить фото авто ${index + 1}`} disabled={busy} onClick={() => void removePhoto(asset)} type="button">×</button>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="carrier-verification__card carrier-verification__documents" data-figma-node={documentsNode}>
        <h2>Документы</h2>
        <div className="carrier-verification__document-grid">
          <DocumentUpload document={identity} documentType="identity" disabled={busy} label="Паспорт (главная страница)" node={blockNode(breakpoint, '2787:50407', '2787:50255')} onUpload={uploadDocument} />
          <DocumentUpload document={selfie} documentType="selfie" disabled={busy} label="Селфи с паспортом" node={blockNode(breakpoint, '2787:50410', '2787:50258')} onUpload={uploadDocument} />
          <DocumentUpload document={driverLicense} documentType="driver_license" disabled={busy} label="Водительские права (если возите на авто)" node={blockNode(breakpoint, '2787:50413', '2787:50261')} onUpload={uploadDocument} />
          {profile?.service_type === 'auto' ? (
            <DocumentUpload document={vehicleRegistration} documentType="vehicle_registration" disabled={busy} label="СТС / регистрация автомобиля" onUpload={uploadDocument} />
          ) : null}
          {profile?.carrier_type === 'company' ? (
            <DocumentUpload document={companyDocument} documentType="company" disabled={busy} label="Документы компании" onUpload={uploadDocument} />
          ) : null}
        </div>
        <div className="carrier-verification__licenses">
          <div className="carrier-verification__licenses-copy">
            <strong>Лицензии (можно несколько)</strong>
            <span>Для авиакурьеров лицензия не требуется. Для остальных — лицензия ИП/ООО на грузоперевозки, международная и т.п.</span>
          </div>
          <div className="carrier-verification__license-rows">
            {licenseTitles.map((_, index) => (
              <div className="carrier-verification__license-row" key={index}>
                <input
                  aria-label={`Название лицензии ${index + 1}`}
                  placeholder={index ? 'Название лицензии' : 'Название лицензии (например: ИП на грузоперевозки)'}
                  value={licenseTitles[index]}
                  onChange={(event) => setLicenseTitles((current) => current.map((value, itemIndex) => itemIndex === index ? event.target.value : value))}
                />
                <UploadTrigger
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="carrier-verification__license-button"
                  disabled={busy}
                  node={blockNode(breakpoint, index ? '2787:50425' : '2787:50421', index ? '2787:50273' : '2787:50269')}
                  onFiles={async ([file]) => uploadDocument(file, 'license', licenseTitles[index])}
                >
                  {licenses[index] ? 'Загружено' : 'Файл'}
                </UploadTrigger>
              </div>
            ))}
            <Button kind="tertiary" size="medium" onClick={() => setLicenseTitles((current) => [...current, ''])}>+ Добавить ещё лицензию</Button>
            <p>Документы используем только для модерации, заказчикам не показываем — они видят только сумму рейтинга верификации.</p>
          </div>
        </div>
      </section>

      <section className="carrier-verification__card carrier-verification__portfolio" data-figma-node={portfolioNode}>
        <h2>Портфолио прошлых перевозок</h2>
        <div className="carrier-verification__info" data-figma-node={blockNode(breakpoint, '2787:50431', '2787:50279')}>
          Загружайте фото своих питомцев и животных, которых уже возили — заказчики видят это первым делом. Минимум 4 фото для прохождения верификации, ориентир для топ-перевозчика — 40+ фото с разных маршрутов и видов животных.
        </div>
        <UploadTrigger
          accept="image/jpeg,image/png,image/webp"
          className="carrier-verification__dropzone carrier-verification__dropzone--portfolio"
          disabled={busy}
          multiple
          node={blockNode(breakpoint, '2787:50434', '2787:50282')}
          onFiles={(files) => uploadPhotos(files.slice(0, Math.max(0, 50 - (profile?.portfolio_photos.length ?? 0))), 'portfolio')}
        >
          <span>Перетащите фото или нажмите для загрузки</span>
          <span>JPG, PNG · до 10 МБ · можно загружать до 50 фото</span>
        </UploadTrigger>
        {(profile?.portfolio_photos ?? []).length > 0 && (
          <div className="carrier-verification__photo-grid" data-figma-node={blockNode(breakpoint, '2787:50436', '2787:50284')}>
            {(profile?.portfolio_photos ?? []).map((asset, index) => (
              <figure key={asset.id}>
                <img src={asset.url?.startsWith('http') || asset.url?.startsWith('/') ? asset.url : `/${asset.url}`} alt={`Фото из портфолио ${index + 1}`} />
                <button aria-label={`Удалить фото ${index + 1}`} disabled={busy} onClick={() => void removePhoto(asset)} type="button">×</button>
              </figure>
            ))}
          </div>
        )}
        <p>Сейчас в портфолио: {profile?.portfolio_photos.length ?? 0} фото · ориентир для топ-перевозчика: 40+ фото</p>
      </section>

      <div className="carrier-verification__actions">
        <WizardButton className="carrier-verification__submit" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Сохраняем…' : 'Отправить на модерацию'}
        </WizardButton>
        {error && <p className="carrier-verification__error" role="alert">{error}</p>}
      </div>
    </div>
  );
}

function VerificationPending({ breakpoint }: { breakpoint: FigmaBreakpoint }) {
  const { status, user } = useAuth();

  useEffect(() => {
    if (status === 'guest') navigate('login');
    if (status === 'authenticated' && !user?.has_carrier_profile) navigate('carrier-rega-email');
  }, [status, user]);

  return (
    <div className="carrier-verification__pending" data-figma-node={blockNode(breakpoint, '2787:50466', '2787:50314')}>
      <div className="carrier-verification__pending-icon" aria-hidden="true">⏳</div>
      <div className="carrier-verification__pending-copy">
        <h1>Документы у нас — проверяем</h1>
        <p>Обычно справляемся за 12–24 часа. Пришлём SMS и письмо, как только всё проверим. Пока — можете смотреть заявки в ленте, но откликаться сможете после верификации.</p>
      </div>
      <section className="carrier-verification__pending-card" data-figma-node={blockNode(breakpoint, '2787:50472', '2787:50320')}>
        <h2>Пока модерация идёт, вы можете</h2>
        <div>
          <Button kind="secondary" size="medium" onClick={() => navigate('carrier-dashboard')}>
            <img alt="" aria-hidden="true" src="/figma/assets/carrier-pending-open.svg" />
            Открыть кабинет (просмотр заявок без откликов)
          </Button>
          <Button kind="secondary" size="medium" onClick={() => navigate('carrier-profile-internal')}>
            <img alt="" aria-hidden="true" src="/figma/assets/carrier-pending-camera.svg" />
            Добавить ещё фото в портфолио
          </Button>
          <Button kind="secondary" size="medium" onClick={() => navigate('carrier-verify')}>
            <img alt="" aria-hidden="true" src="/figma/assets/carrier-pending-edit.svg" />
            Доредактировать данные
          </Button>
        </div>
      </section>
    </div>
  );
}

export function CarrierVerificationScreen({ screen }: { screen: CarrierVerificationScreenId }) {
  const breakpoint = useFigmaBreakpoint();
  return (
    <VerificationShell breakpoint={breakpoint} screen={screen}>
      {screen === 'carrier-verify'
        ? <VerificationForm breakpoint={breakpoint} />
        : <VerificationPending breakpoint={breakpoint} />}
    </VerificationShell>
  );
}
